import { Progress } from 'antd';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Delete, Minus, Plus } from 'react-feather';
import DefaultButton from '../../components/buttons/DefaultButton';
import AnalyticsChart from '../../components/charts/stats/AnalyticsCart';
import TextHeader from '../../components/headers/TextHeader';
import BarChartSkeleton from '../../components/loading/BarChartSkeleton';
import HeaderSkeleton from '../../components/loading/HeaderSkeleton';
import TableSkeleton from '../../components/loading/TableSkeleton';
import Table from '../../components/tables/Table';
import TableItem from '../../components/tables/TableItem';
import TableRow from '../../components/tables/TableRow';
import TableWrapper from '../../components/tables/TableWrapper';
import Tabs from '../../components/tabs/Tabs';
import Tag from '../../components/tags/Tag';
import Trending from '../../components/tags/Trending';
import Typography from '../../components/typography/Typography';
import { useCompany } from '../../hooks/useCompany';
import { LicenseData, LicenseStatProduct } from '../../interfaces/Licenses';
import SelectUser from '../../organisms/users/SelectUser';
import { LicenseService } from '../../services/licenses/Licenses';
import { UserNamesInterface, UsersService } from '../../services/users/Users';
import ListComponent from '../../utils/ListComponent';
import { PriceUtils } from '../../utils/numbers/PriceUtils';
import CurrencyFormatter from '../../utils/text/CurrencyFormatter';
interface props {
  data: {
    data: {
      stats: LicenseStatProduct;
      allStats: LicenseStatProduct[];
    };
  };
}

const Information: FC<{
  license: any;
  currentMonth: string;
  setCurrentMonth: (m: string) => void;
}> = ({ license, currentMonth, setCurrentMonth }) => {
  if (!license) {
    return (
      <>
        <HeaderSkeleton />
        <div className="my-4" />
        <BarChartSkeleton bars={6} />
        <div className="my-4" />
        <TableSkeleton columns={4} rows={6} />
      </>
    );
  }

  return (
    <div className="my-4">
      {license.allStats && (
        <>
          <AnalyticsChart data={license.allStats} activeKey={currentMonth} />
          <div className="mt-4" />
          <TableWrapper>
            <Table header={['periode', 'aantal', 'eenheidsprijs', 'Totaal']}>
              <ListComponent
                data={license.allStats}
                renderItem={(item, index) => (
                  <TableRow
                    key={index}
                    onEnter={() => setCurrentMonth(item.month)}
                    onLeave={() => setCurrentMonth('')}
                  >
                    <TableItem>
                      {item.month.split('#')[1]}/{item.month.split('#')[0]}
                    </TableItem>
                    <TableItem className="flex items-center gap-2">
                      <div className="flex items-center">
                        <Typography tag="span" type="label" addClass={'mr-2'}>
                          {item.amount}
                        </Typography>
                        {index > 0 && (
                          <Trending
                            current={item.amount}
                            previous={license.allStats[index - 1].amount}
                          />
                        )}
                      </div>
                    </TableItem>
                    <TableItem>
                      <CurrencyFormatter price={item.price} />
                    </TableItem>
                    <TableItem>
                      {(!license?.stats?.yearly ||
                        index === license.allStats.length - 1) && (
                        <CurrencyFormatter
                          price={PriceUtils.rounded(
                            item.price * item.amount,
                            2
                          )}
                        />
                      )}
                    </TableItem>
                  </TableRow>
                )}
              />
            </Table>
          </TableWrapper>
        </>
      )}
    </div>
  );
};

const UserListTab: FC<{
  users?: any[];
  totalAssignedUsers: number;
  maxAmount?: number;
  percentageUsed: number;
  allUsers?: UserNamesInterface[];
  newUsers: number[];
  onUpdate: (userId: number, delta: number) => void;
  onUserSelect: (id: number) => void;
  onUserSubmit: () => void;
  loading?: boolean;
}> = ({
  users,
  totalAssignedUsers,
  maxAmount,
  percentageUsed,
  onUpdate,
  allUsers,
  onUserSelect,
  onUserSubmit,
  newUsers,
  loading,
}) => {
  return (
    <div>
      <div className="flex flex-col gap-6 w-full">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 w-full bg-slate-50/70 dark:bg-white/5 p-4 rounded-xl border border-white/10">
          <div className="flex flex-col min-w-[140px]">
            <Typography
              type="label"
              tag="span"
              className="text-xs uppercase tracking-wider opacity-60"
            >
              Toegewezen
            </Typography>
            <Typography type="h4" tag="span" className="font-bold text-lg">
              {totalAssignedUsers}{' '}
              <span className="text-sm font-normal opacity-50">
                / {maxAmount || 0}
              </span>
            </Typography>
          </div>

          <div className="flex-grow pt-1">
            <Progress
              percent={Math.min(percentageUsed, 100)}
              strokeLinecap="round"
              strokeWidth={10}
              trailColor="rgba(128, 139, 196, 0.1)"
              showInfo={true}
              status={percentageUsed > 100 ? 'exception' : 'normal'}
              className="m-0"
            />
          </div>
        </div>

        {allUsers && (
          <div className="flex flex-col gap-2">
            <Typography
              tag="span"
              type="label"
              className="ml-1 text-xs font-medium opacity-70"
            >
              Selecteer gebruikers
            </Typography>

            <div className="flex items-start gap-3">
              <div className="flex-grow">
                <SelectUser
                  users={allUsers}
                  onSelect={(val: any) => onUserSelect(val)}
                  mode="multiple"
                  value={newUsers}
                  error={''}
                />
              </div>
              <div className="h-[38px] flex items-center">
                {' '}
                <DefaultButton
                  style="blue"
                  size="small"
                  onclick={onUserSubmit}
                  className="whitespace-nowrap shadow-md hover:shadow-lg transition-shadow"
                >
                  Assign users
                </DefaultButton>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4">
        {!users ? (
          <TableSkeleton rows={7} columns={3} />
        ) : users.length === 0 ? (
          <div className="py-10 text-center border border-dashed border-gray-200 rounded-lg">
            <Typography type="label" tag="p">
              Geen gebruikers toegewezen
            </Typography>
          </div>
        ) : (
          <Table header={['Gebruiker', 'Toegewezen']}>
            <ListComponent
              data={users}
              renderItem={(user) => (
                <TableRow key={user.id_user}>
                  <TableItem>
                    {user.geb_voornaam} {user.geb_naam}
                  </TableItem>
                  <TableItem>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => onUpdate(user.id_user, -1, user.id)}
                      >
                        {user.licentie_amount > 1 ? (
                          <Minus size={16} />
                        ) : (
                          <Delete size={16} className="text-red-300" />
                        )}
                      </button>
                      <span className="w-4 text-center">
                        {user.licentie_amount}
                      </span>
                      <button
                        onClick={() => onUpdate(user.id_user, 1, user.id)}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </TableItem>
                </TableRow>
              )}
            />
          </Table>
        )}
      </div>
    </div>
  );
};

const LicenseStatDetail: FC<props> = (data) => {
  const [license, setLicense] = useState<LicenseStatProduct>();
  const [licenseInfo, setLicenseInfo] = useState<LicenseData>();
  const [totalAssignedUsers, setTotalAssignedUsers] = useState<number>(0);
  const [percentageUsed, setPercentageUsed] = useState<number>(0);
  const [users, setUsers] = useState<any[]>();
  const [currentMonth, setCurrentMonth] = useState<string>('');
  const [allUsers, setAllusers] = useState<UserNamesInterface[]>();
  const [newUsers, setNewUsers] = useState<number[]>([]);
  const [loading, setLoading] = useState<Boolean>(false);
  const { companyId } = useCompany();
  const licensesService = useMemo(() => new LicenseService(), []);
  const userService = new UsersService();

  const fetchByProductId = async () => {
    if (!companyId) return;

    const result = await licensesService.getCompanyLicenceByProductId(
      companyId,
      data.data.stats.product_id
    );

    console.log(result);

    const allUsers = await fetchUsers();
    const assignedUserIds = new Set(result.users.map((u: any) => u.id_user));
    const unassignedUsers = allUsers.filter(
      (user: any) => !assignedUserIds.has(user.value)
    );

    setLicenseInfo(result.license);
    setUsers(result.users);
    setAllusers(unassignedUsers);

    const totalAssigned = result.users.reduce(
      (n: number, { licentie_amount }: any) => n + licentie_amount,
      0
    );

    setTotalAssignedUsers(totalAssigned);
    setPercentageUsed(
      Math.round((totalAssigned / data.data.stats.amount) * 100)
    );
  };

  const updateUserLicense = async (
    userId: number,
    delta: number,
    id: number
  ) => {
    if (!users) return;

    const updatedUsers = users.map((user) =>
      user.id_user === userId
        ? {
            ...user,
            licentie_amount: Math.max(user.licentie_amount + delta, 0),
          }
        : user
    );

    const updatedUser = updatedUsers.find((u) => u.id_user === userId);
    const newLicenseAmount = updatedUser ? updatedUser.licentie_amount : 0;

    const totalAssigned = updatedUsers.reduce(
      (sum, u) => sum + u.licentie_amount,
      0
    );

    try {
      licensesService.updateLicense(companyId, id, {
        licentie_amount: newLicenseAmount,
      });
    } catch (error) {
      console.log(error);
    }

    setUsers(updatedUsers);
    setTotalAssignedUsers(totalAssigned);
    setPercentageUsed(
      Math.round((totalAssigned / (data.data.stats.amount || 1)) * 100)
    );
  };

  const fetchUsers = async (): Promise<UserNamesInterface[]> => {
    try {
      const response = await userService.getUserNames(companyId as number);
      return response;
    } catch (error) {
      console.error('Error fetching usernames:', error);
      return [];
    }
  };

  const onUserSelect = (selectedIds: number | number[]) => {
    if (Array.isArray(selectedIds)) {
      setNewUsers(selectedIds);
    } else {
      setNewUsers([selectedIds]);
    }
  };

  const addSelectedUsers = async () => {
    if (!license || !companyId || newUsers.length === 0) return;

    setLoading(true);
    try {
      const addPromises = newUsers.map((userId) =>
        licensesService.addUserLicense(
          license.stats.product_id,
          userId,
          companyId
        )
      );
      await Promise.all(addPromises);
      setNewUsers([]);
      await fetchByProductId();
    } catch (error) {
      console.error('Error adding users:', error);
    } finally {
      setLoading(true);
    }
  };

  useEffect(() => {
    setLicense(data.data);
    fetchByProductId();
    fetchUsers();
  }, [data.data, companyId]);

  const tabs = useMemo(
    () => [
      {
        nav: 'Informatie',
        component: (
          <Information
            license={license}
            currentMonth={currentMonth}
            setCurrentMonth={setCurrentMonth}
          />
        ),
      },
      {
        nav: 'Toegewezen gebruikers',
        component: (
          <UserListTab
            users={users}
            allUsers={allUsers}
            newUsers={newUsers}
            totalAssignedUsers={totalAssignedUsers}
            maxAmount={license?.stats?.amount}
            percentageUsed={percentageUsed}
            onUpdate={updateUserLicense}
            onUserSelect={onUserSelect}
            onUserSubmit={addSelectedUsers}
            loading={loading}
          />
        ),
      },
    ],
    [
      license,
      users,
      totalAssignedUsers,
      percentageUsed,
      currentMonth,
      allUsers,
      newUsers,
    ]
  );

  if (!companyId) return <div>Error: No company selected</div>;

  return (
    <div>
      <div className="mb-4">
        {license?.stats?.yearly ? (
          <Tag label={'Yearly'} style={'telecom'} />
        ) : (
          <Tag label={'Monthly'} style={'sales'} />
        )}
      </div>
      <TextHeader title={license?.name} subtitle={license?.stats?.category} />
      <div className="py-2">
        <Typography tag="p" type="label">
          {licenseInfo?.lic_informatie}
        </Typography>
      </div>
      {license?.stats?.pack && (
        <div className="py-5 border-t border-gray-100 mt-4">
          <Typography tag="p" type="bold">
            Samengestelde licentie
          </Typography>
          <ListComponent
            data={license.stats.pack}
            renderItem={(item) => (
              <Typography tag="p" type="label">
                {item}
              </Typography>
            )}
          />
        </div>
      )}
      <Tabs tabs={tabs} />
    </div>
  );
};

export default LicenseStatDetail;
